import { ref, uploadBytesResumable } from 'firebase/storage';
import { app, auth, storage } from './firebaseConfig';

export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

// Must stay in sync with the contentType allowlist in storage.rules. `image/*`
// is deliberately NOT used: it also matches image/svg+xml, which executes
// script when opened from a download URL.
const ALLOWED_TYPES = [
  'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif',
];

const EXT_BY_TYPE = {
  'image/jpeg': 'jpg',
  'image/png':  'png',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'image/heif': 'heif',
};

const RETRYABLE = new Set([
  'storage/retry-limit-exceeded',
  'storage/server-file-wrong-size',
  'storage/unknown',
]);

const FRIENDLY = {
  'storage/unauthorized':
    "We couldn't accept that photo. Please choose a JPG or PNG under 5 MB and try again.",
  'storage/unauthenticated':
    'Your session expired. Please reload the page and submit again.',
  'storage/unauthorized-app':
    "We couldn't verify your browser. Turn off any ad or tracker blocker for this site, then try again.",
  'storage/quota-exceeded':
    'Our storage is temporarily full. Please try again in a few minutes.',
  'storage/retry-limit-exceeded':
    'The upload timed out. Check your internet connection and try again.',
  'storage/invalid-checksum':
    'The photo was corrupted on the way up. Please try again.',
  'storage/canceled':
    'Upload cancelled.',
  'permission-denied':
    "We couldn't save your application. Please reload the page and try again.",
  'unavailable':
    'Network problem. Check your internet connection and try again.',
};

export function friendlyMessage(err) {
  const code = err?.code;
  if (code && FRIENDLY[code]) return FRIENDLY[code];
  if (!navigator.onLine) return FRIENDLY['unavailable'];
  return 'Something went wrong. Please try again — if it keeps failing, contact us on WhatsApp.';
}

// iOS Safari hands back an empty File.type for HEIC pictures straight off the
// camera roll, and some Android WebViews report application/octet-stream. The
// Storage rule matches on contentType, so a wrong value is an instant denial:
// sniff the magic bytes rather than trusting the browser.
async function sniffType(file) {
  let head;
  try {
    head = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  } catch {
    return null;
  }
  const bytes = (...v) => v.every((b, i) => head[i] === b);
  const ascii = (at, s) => [...s].every((c, i) => head[at + i] === c.charCodeAt(0));

  if (bytes(0xff, 0xd8, 0xff)) return 'image/jpeg';
  if (bytes(0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)) return 'image/png';
  if (ascii(0, 'RIFF') && ascii(8, 'WEBP')) return 'image/webp';
  // ISO-BMFF container: HEIC/HEIF share the `ftyp` box; the brand at offset 8
  // tells them apart from MP4/MOV.
  if (ascii(4, 'ftyp')) {
    const brand = String.fromCharCode(head[8], head[9], head[10], head[11]);
    if (['heic', 'heix', 'hevc', 'heim', 'heis', 'hevm', 'hevs'].includes(brand)) {
      return 'image/heic';
    }
    if (['mif1', 'msf1'].includes(brand)) return 'image/heif';
  }
  return null;
}

// The single source of truth for "is this an acceptable image". Used by the
// form's file picker (fail fast, before the user fills the rest in) and again
// by uploadImage (the picker can be bypassed).
export async function inspectImage(file) {
  if (!file) return { ok: false, reason: 'Please choose a photo.' };
  if (file.size === 0) {
    return { ok: false, reason: 'That file is empty. Please choose another photo.' };
  }
  if (file.size > MAX_IMAGE_BYTES) {
    const mb = (file.size / 1024 / 1024).toFixed(1);
    return { ok: false, reason: `That photo is ${mb} MB. Please choose one under 5 MB.` };
  }
  const declared = (file.type || '').toLowerCase();
  const contentType = (await sniffType(file))
    || (ALLOWED_TYPES.includes(declared) ? declared : null);
  if (!contentType) {
    return { ok: false, reason: 'Please choose a JPG, PNG, WEBP or HEIC photo.' };
  }
  return { ok: true, contentType, size: file.size };
}

// Everything a support ticket needs to be answerable without a repro.
export function storageDiagnostics(extra = {}) {
  const u = auth.currentUser;
  return {
    firebaseApp:   app.name,
    bucket:        app.options.storageBucket,
    storageHost:   storage.host,
    appCheck:      Boolean(process.env.REACT_APP_RECAPTCHA_SITE_KEY),
    authUser:      u ? { uid: u.uid, email: u.email, phone: u.phoneNumber } : null,
    online:        navigator.onLine,
    platform:      navigator.userAgentData?.platform || navigator.platform,
    userAgent:     navigator.userAgent,
    ...extra,
  };
}

export function logStorageFailure(stage, err, extra) {
  const diag = storageDiagnostics({
    stage,
    errorCode:      err?.code || err?.name || 'unknown',
    errorMessage:   err?.message,
    httpStatus:     err?.status ?? err?.customData?.serverResponse?.status,
    serverResponse: err?.serverResponse || err?.customData?.serverResponse || null,
    ...extra,
  });
  console.error(`[tfrx:${stage}]`, diag);
  window.gtag?.('event', 'upload_failure', {
    stage,
    error_code: diag.errorCode,
    platform: diag.platform,
  });
  return diag;
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Uploads an image and resolves to its Storage path. Deliberately does NOT call
 * getDownloadURL: that is a `read`, which the public signup pages are not
 * allowed to perform (and must not be — it is what lets a photo stay private to
 * admins). The path goes into Firestore; authenticated readers resolve it.
 *
 * Each retry writes to a fresh path. The Storage rules are create-only (no
 * overwrite), so retrying onto a path a previous attempt already landed
 * server-side would itself be denied as storage/unauthorized. All attempts
 * share the `{prefix}/{id}` prefix, which is what the account-deletion and
 * purge jobs sweep.
 */
export async function uploadImage(file, {
  pathPrefix,
  id,
  onProgress,
  attempts = 3,
}) {
  const check = await inspectImage(file);
  if (!check.ok) {
    const err = new Error(check.reason);
    err.code = 'storage/invalid-argument';
    err.friendly = check.reason;
    throw err;
  }
  const { contentType } = check;
  const ext = EXT_BY_TYPE[contentType];

  let lastErr;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    const path = attempt === 1
      ? `${pathPrefix}/${id}.${ext}`
      : `${pathPrefix}/${id}_r${attempt}.${ext}`;

    try {
      const task = uploadBytesResumable(ref(storage, path), file, {
        contentType,
        cacheControl: 'public, max-age=31536000',
      });
      if (onProgress) {
        task.on('state_changed', (snap) => {
          onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100));
        });
      }
      const snap = await task;
      console.info('[tfrx:upload] ok', {
        path: snap.metadata.fullPath,
        contentType: snap.metadata.contentType,
        size: snap.metadata.size,
        attempt,
      });
      return { path, contentType, size: file.size };
    } catch (err) {
      lastErr = err;
      logStorageFailure('upload', err, {
        path, contentType, declaredType: file.type, size: file.size, attempt,
      });
      // A rules denial will fail identically forever — retrying only makes the
      // user wait longer for the same error.
      if (!RETRYABLE.has(err?.code) || attempt === attempts) break;
      await sleep(2 ** (attempt - 1) * 800);
    }
  }
  lastErr.friendly = friendlyMessage(lastErr);
  throw lastErr;
}
